import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import {
    Map, MapControl, ControlPosition, useMap, useApiIsLoaded,
    Polyline,
} from '@vis.gl/react-google-maps';
import CustomButton from './MapShapes/MapCustomButton';
import CustomViewButton from './MapShapes/MapCustomViewButton';
import MapRightClickMenu from './MapShapes/RightClickMenu/AddressWindow';
import MapShapeHandler from './MapShapes/MapShapeHandler';
import CustomInfoWindow from './MapShapes/CustomInfoWindow';
import KmlLayer from './MapShapes/KmlLayer';
import { zooomToMarkes, resetMapControlState, createControlState } from './mapControls';
import config from '../constants/Config';
import { getCurrentPosition, getLocalStorageValue } from '../helper-classes/utility-functions';
import { coordinate } from './constants.map';
import mapStyles from './MapsWrapper.module.scss';
import customStyles from '../index.module.scss';

/**
 * MapsWrapper - the app's only map shell, built on
 * '@vis.gl/react-google-maps'.
 * Covers: map shell, shapes, custom overlays/markers, custom controls,
 * right-click address menu, traffic layer.
 */

const DEFAULT_HEIGHT = 'calc(100vh - 46px)';
const showMapRefreshButton = config.get('SHOW_MAP_REFRESH_BUTTON');

/**
 * `latlng` (usually `props.coordinates`) wins when it carries a real value;
 * `fallback` is the caller's own per-instance browser-geolocation state (see
 * MapsWrapper's `browserCoordinates`) or the static default, so nothing here
 * is shared across MapsWrapper instances.
 */
const getCoordinates = (latlng: Object, fallback: Object) => {
    let location = fallback;
    if (latlng && (latlng.lat || latlng.lng)) {
        location = latlng;
        location.lat = location.lat || 0;
        location.lng = location.lng || 0;
    }
    return location;
};

// vis.gl gives a LatLngLiteral on event.detail.latLng, the raw Maps API gives a
// LatLng with lat()/lng() accessors. Accept either so the handler survives both.
const readLatLng = (latLng: Object) => {
    if (!latLng) return null;
    const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
    const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    return { lat, lng };
};

/**
 * Traffic layer, created imperatively - '@vis.gl' has no <TrafficLayer>.
 */
const TrafficLayer = () => {
    const map = useMap();
    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;
        const layer = new window.google.maps.TrafficLayer({ autoRefresh: true });
        layer.setMap(map);
        return () => layer.setMap(null);
    }, [map]);
    return null;
};

const DirectionsRoute = (props: Object) => {
    const { directions } = props;

    const coordinates = directions?.polyline?.geoJsonLinestring?.coordinates;

    if (!coordinates || coordinates.length === 0) return null;

    const path = coordinates.map(([lng, lat]) => ({ lat, lng }));

    return (
        <Polyline
            path={path}
            strokeColor="#4285F4"
            strokeOpacity={0.9}
            strokeWeight={5}
        />
    );
};

/**
 * Everything that needs the map instance. Rendered as a child of <Map> so
 * useMap() resolves.
 */
const MapContent = (props: Object) => {
    const map = useMap();
    const isApiLoaded = useApiIsLoaded();
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const hasNotifiedLoaded = useRef(false);
    const lastCenterRef = useRef(null);
    const lastZoomRef = useRef(null);
    const {
        getBoundaries, isLoaded, markers, zoom, appliedFilters,
        coordinates, browserCoordinates, boundaryChanged, onZoomChanged,
    } = props;
    // Owns the show/hide + resize button state for this map instance. A
    // useRef here (rather than a WeakMap keyed off the map instance) works
    // because MapContent is the only caller into mapControls.js's stateful
    // functions - the ref's lifetime is naturally tied to this component's,
    // so it needs no external cleanup keyed by the map instance.
    const controlStateRef = useRef(createControlState());

    // Clear the controls actually pushed onto `map` when this unmounts.
    // Otherwise a remount that reuses the same google.maps.Map instance
    // re-adds every custom control on top of the old ones, rendering them
    // twice.
    useEffect(() => () => resetMapControlState(map, controlStateRef), [map]);

    const editableCircleRef = useRef(null);
    const editableRectangleRef = useRef(null);
    const editablePolygonRef = useRef(null);
    const circleRef = useRef(null);
    const lastEditableShapeRef = useRef(null);

    useEffect(() => {
        if (!map || !isApiLoaded) return;
        if (!hasNotifiedLoaded.current) {
            hasNotifiedLoaded.current = true;
            setIsGoogleLoaded(true);
            // Legacy passes window.google here, but every consumer that uses the
            // argument (Home) stores it and calls getBounds/fitBounds on it, so
            // the map instance is what is actually wanted. The others ignore it.
            if (typeof getBoundaries === 'function') getBoundaries(map);
            if (typeof isLoaded === 'function') isLoaded(map);
        }
    }, [map, isApiLoaded, getBoundaries, isLoaded]);

    // Auto-fit + shape-specific custom controls, shared with the legacy shell.
    // zooomToMarkes needs the full props object (legacy signature); depending on
    // it directly would re-run this effect on every render, so we only track the
    // fields below that should actually trigger it.
    useEffect(() => {
        if (!map || !isGoogleLoaded) return;
        zooomToMarkes(map, { ...props, isGoogleLoaded: true }, controlStateRef, {
            CircleRef: circleRef,
            editableCircleRef,
            editableRectangleRef,
            editablePolygonRef,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isGoogleLoaded, markers, zoom, appliedFilters]);

    // Fit bounds when editable shapes are resized/moved, not just on type changes
    useEffect(() => {
        if (!map || !window.google || !markers?.shape) return;
        const type = markers.shape?.type?.toLowerCase();
        if (!['editablecircle', 'editablerectangle', 'editablepolygon'].includes(type)) return;

        const shapeKey = JSON.stringify({
            type,
            props: markers.shape.props,
        });

        if (shapeKey === lastEditableShapeRef.current) return;
        lastEditableShapeRef.current = shapeKey;

        const fitBounds = () => {
            if (type === 'editablecircle' && editableCircleRef?.current?.CircleRef?.current?.getBounds?.()) {
                map.fitBounds(editableCircleRef.current.CircleRef.current.getBounds());
            } else if (type === 'editablerectangle' && editableRectangleRef?.current?.RectangleRef?.current?.getBounds?.()) {
                map.fitBounds(editableRectangleRef.current.RectangleRef.current.getBounds());
            } else if (type === 'editablepolygon' && editablePolygonRef?.current?.PolygonRef?.current?.getPath?.()) {
                const bounds = new window.google.maps.LatLngBounds();
                const paths = editablePolygonRef.current.PolygonRef.current.getPath();
                for (let i = 0; i < paths.getLength(); i += 1) {
                    bounds.extend(paths.getAt(i));
                }
                map.fitBounds(bounds);
            }
        };

        requestAnimationFrame(fitBounds);
    }, [map, markers]);

    // Recentre imperatively rather than making <Map> a controlled component,
    // which would fight the user's own panning.
    useEffect(() => {
        if (!map) return;
        const shouldAutoFit = markers?.shape?.type?.toLowerCase() === 'nearestassetlandmark';
        if (shouldAutoFit) return;
        const target = getCoordinates(coordinates, browserCoordinates);
        if (!target || (!target.lat && !target.lng)) return;
        const key = `${target.lat},${target.lng}`;
        if (key === lastCenterRef.current) return;
        lastCenterRef.current = key;
        map.panTo({ lat: Number(target.lat), lng: Number(target.lng) });
    }, [map, coordinates, browserCoordinates, markers?.shape?.type]);

    // Re-zoom imperatively for the same reason as the recentre effect above -
    // `<Map>`'s defaultZoom only applies on initial mount, so bookmark/asset
    // zoom-level changes need to be applied to the live map instance directly.
    useEffect(() => {
        if (!map || !zoom) return;
        if (zoom === lastZoomRef.current) return;
        lastZoomRef.current = zoom;
        map.setZoom(zoom);
    }, [map, zoom]);

    useEffect(() => {
        if (!map || typeof boundaryChanged !== 'function') return undefined;
        const listener = map.addListener('tilesloaded', () => boundaryChanged());
        return () => listener.remove();
    }, [map, boundaryChanged]);

    useEffect(() => {
        if (!map || typeof onZoomChanged !== 'function') return undefined;
        const listener = map.addListener('zoom_changed', () => {
            const center = map.getCenter();
            onZoomChanged({
                zoomLevel: map.getZoom(),
                lat: center ? center.lat() : 0,
                lng: center ? center.lng() : 0,
            });
        });
        return () => listener.remove();
    }, [map, onZoomChanged]);

    const showTraffic = (props.showTrafficLayerState && !props.hideViewButton)
        || props.defaultTrafficLayerView;

    return (
        <React.Fragment>
            {showTraffic && <TrafficLayer />}
            {Array.from(new Set(props.kmlPreferenceUrls || [])).map(url => (
                <KmlLayer
                    key={url}
                    url={url}
                    zoomToLayer={props.zoomToKmlLayer === url}
                    zoomRequest={props.zoomToKmlLayerRequest}
                />
            ))}

            {props.markers?.directions?.path &&
                <DirectionsRoute
                    directions={props.markers.directions}
                />
            }

            {!props.hideViewButton &&
                <MapControl
                    position={ControlPosition.TOP_CENTER}
                    className={(() => {
                        const willShowRefreshButton = props.refreshData
                            && typeof props.refreshData === 'function'
                            && showMapRefreshButton;
                        if (typeof props.bookmarkClick === 'function') return mapStyles['maptype-control-position'];
                        return willShowRefreshButton
                            ? mapStyles['maptype-control-position-view']
                            : mapStyles['maptype-control-position-view-no-refresh'];
                    })()}
                >
                    <div
                        id="maptype-control-view"
                        className={mapStyles['maptype-control-custom']}
                    >
                        <CustomViewButton
                            handleClick={props.handleViewButtonClick}
                            viewTraffic="true"
                            viewAssetLabel={props.viewAssetLabel}
                            viewLandmarkLabel={props.viewLandmarkLabel}
                            manageClustering={props.manageClustering}
                            showKml={props.showKml}
                            showAssetClusteringOption={props.showAssetClusteringOption}
                            showLandmarkClusteringOption={props.showLandmarkClusteringOption}
                            snapRoutesCallback={props.snapRoutesCallback}
                            bookmarkClick={props.bookmarkClick}
                            mapInfo={{
                                center: map ? map.getCenter() : {},
                                zoom: map ? map.getZoom() : 3,
                            }}
                            trackAnalytics={(...d) => {
                                if (typeof props.trackAnalytics === 'function') props.trackAnalytics(...d);
                            }}
                        />
                    </div>
                </MapControl>
            }

            {props.refreshData && typeof props.refreshData === 'function' && showMapRefreshButton &&
                <MapControl position={ControlPosition.TOP_LEFT}>
                    <div
                        id="maptype-control"
                        className={`${mapStyles.controls} ${mapStyles['maptype-control']} ${customStyles['gm-view-button']}`}
                    >
                        <CustomButton
                            refreshData={() => {
                                if (map) props.refreshData(map.getZoom(), map.getCenter());
                            }}
                            resetRefreshTimer={props.resetRefreshTimer}
                            trackAnalytics={(...d) => {
                                if (typeof props.trackAnalytics === 'function') props.trackAnalytics(...d);
                            }}
                        />
                    </div>
                </MapControl>
            }

            {!props.hideRightClick &&
                <div style={{ display: 'none' }}>
                    <MapRightClickMenu
                        addressParameter={(addressParameter) => {
                            // eslint-disable-next-line no-param-reassign
                            props.addressWindowSetterRef.current = addressParameter;
                        }}
                    />
                </div>
            }

            {(isGoogleLoaded && props.markers) &&
                <MapShapeHandler
                    marker={props.markers}
                    google={window.google}
                    mapRef={map}
                    CircleRef={circleRef}
                    editableCircleRef={editableCircleRef}
                    editableRectangleRef={editableRectangleRef}
                    editablePolygonRef={editablePolygonRef}
                    openAddressWindow={props.openAddressWindow}
                    closeAddressWindow={props.closeAddressWindow}
                    showAssetLabel={props.showAssetLabel}
                    showLandmarkLabel={props.showLandmarkLabel}
                    showClusterInfo={props.showClusterInfo}
                    findClusterExplodePoint={props.findClusterExplodePoint}
                    zoomLevel={map ? map.getZoom() : 3}
                    moduleName={props.moduleName || ''}
                    tripIconClick={props.tripIconClick}
                    tripInfoBox={props.tripInfoBox}
                    selectedDirectionStep={props.selectedDirectionStep}
                    onDirectionStepClosed={props.onDirectionStepClosed}
                />
            }

            {props.tripInfoBox?.locationSegment &&
                <CustomInfoWindow tripInfoBox={props.tripInfoBox} />
            }
        </React.Fragment>
    );
};

/**
 * Renders the map once the Maps script has actually finished loading.
 *
 * The control options below reference enum objects (MapTypeControlStyle,
 * ControlPosition) that do not exist on window.google.maps until the script is
 * fully parsed. APIProvider loads it asynchronously, so reading them during the
 * first render throws. This mirrors the legacy shell's `loadingElement`.
 */
const MapShell = (props: Object) => {
    const isApiLoaded = useApiIsLoaded();
    const height = props.height ? props.height : DEFAULT_HEIGHT;

    if (!isApiLoaded || !window.google?.maps?.MapTypeControlStyle) {
        return <div id="loadingElement" style={{ height }} />;
    }

    const { ControlPosition: GControlPosition, MapTypeControlStyle } = window.google.maps;

    return (
        <Map
            style={{ width: '100%', height: '100%' }}
            defaultZoom={props.zoom ? props.zoom : 3}
            defaultCenter={props.initialCenter}
            mapTypeControl
            mapTypeControlOptions={{
                style: MapTypeControlStyle.HORIZONTAL_BAR,
                position: GControlPosition.LEFT_TOP,
            }}
            fullscreenControl={false}
            rotateControl={false}
            zoomControlOptions={{ position: GControlPosition.LEFT_BOTTOM }}
            streetViewControlOptions={{ position: GControlPosition.LEFT_BOTTOM }}
            minZoom={3}
            maxZoom={21}
            onContextmenu={props.openAddressWindow}
            onClick={props.closeAddressWindow}
        >
            <MapContent {...props} />
        </Map>
    );
};

const MapsWrapper = (props: Object) => {
    const height = props.height ? props.height : DEFAULT_HEIGHT;
    const [showTrafficLayerState, setShowTrafficLayerState] = useState(getLocalStorageValue('mapTrafficLayer') === 'true');
    const [showAssetLabel, setShowAssetLabel] = useState(getLocalStorageValue('mapAssetLabel') !== 'false');
    const [showLandmarkLabel, setShowLandmarkLabel] = useState(getLocalStorageValue('mapLandmarkLabel') !== 'false');

    // Per-instance fallback center, used only when the caller doesn't pass its
    // own `coordinates` prop. Previously this lived in a module-level `let`
    // shared by every MapsWrapper on the page, which never re-rendered any
    // already-mounted map once geolocation resolved. State here fixes both:
    // it's scoped to this instance and resolving it triggers a re-render.
    const [browserCoordinates, setBrowserCoordinates] = useState(coordinate);
    useEffect(() => {
        let cancelled = false;
        getCurrentPosition()
            .then((response) => {
                if (cancelled) return;
                const { latitude, longitude } = response.coords;
                setBrowserCoordinates({ lat: latitude, lng: longitude });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Per-instance mutable state for the right-click address window,
    // previously a module-level `let` shared by every MapsWrapper on the
    // page - which meant two maps mounted at once (e.g. a live map plus
    // PrintEventsList's own MapsWrapper) fought over the same address-window
    // setter.
    const addressWindowSetterRef = useRef(null);

    const openAddressWindow = useCallback((event: Object) => {
        const latLng = readLatLng(event?.detail ? event.detail.latLng : event?.latLng);
        if (latLng && typeof addressWindowSetterRef.current === 'function') {
            addressWindowSetterRef.current(latLng.lat, latLng.lng, true);
        }
        return true;
    }, []);

    const closeAddressWindow = useCallback(() => {
        if (typeof addressWindowSetterRef.current === 'function') {
            addressWindowSetterRef.current(0, 0, false);
        }
        return true;
    }, []);

    const handleViewButtonClick = (key, flag) => {
        switch (key) {
        case 'mapAssetLabel': setShowAssetLabel(flag); break;
        case 'mapLandmarkLabel': setShowLandmarkLabel(flag); break;
        case 'mapTrafficLayer': setShowTrafficLayerState(flag); break;
        default: break;
        }
    };

    const initialCenter = getCoordinates(props.coordinates, browserCoordinates);

    return (
        <div id="containerElement" style={{ height }}>
            <MapShell
                {...props}
                initialCenter={initialCenter}
                browserCoordinates={browserCoordinates}
                showTrafficLayerState={showTrafficLayerState}
                showAssetLabel={showAssetLabel}
                showLandmarkLabel={showLandmarkLabel}
                handleViewButtonClick={handleViewButtonClick}
                openAddressWindow={openAddressWindow}
                closeAddressWindow={closeAddressWindow}
                addressWindowSetterRef={addressWindowSetterRef}
            />
        </div>
    );
};

export default MapsWrapper;
